import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { filterMajors } from '../data/undergraduateMajors';
import './MajorMinorCombobox.css';

type Props = {
  id: string;
  value: string;
  onChange: (next: string) => void;
};

export const MajorMinorCombobox: React.FC<Props> = ({ id, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  const rootRef = useRef<HTMLDivElement>(null);
  const blurCommitTimerRef = useRef<number | null>(null);
  const draftRef = useRef(draft);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const filtered = useMemo(() => filterMajors(draft, 100), [draft]);

  const clearBlurTimer = () => {
    if (blurCommitTimerRef.current !== null) {
      window.clearTimeout(blurCommitTimerRef.current);
      blurCommitTimerRef.current = null;
    }
  };

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => () => clearBlurTimer(), []);

  const commit = useCallback(
    (next: string) => {
      clearBlurTimer();
      const t = next.trim();
      onChange(t);
      setDraft(t);
      setOpen(false);
    },
    [onChange]
  );

  const scheduleBlurCommit = useCallback(() => {
    clearBlurTimer();
    blurCommitTimerRef.current = window.setTimeout(() => {
      blurCommitTimerRef.current = null;
      const t = draftRef.current.trim();
      onChange(t);
      setDraft(t);
      setOpen(false);
    }, 120);
  }, [onChange]);

  return (
    <div className="profile-major-combo" ref={rootRef}>
      <div className="profile-major-field">
        <input
          id={id}
          type="text"
          role="combobox"
          className="profile-major-input"
          autoComplete="off"
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? `${id}-major-listbox` : undefined}
          placeholder="Search or type your major / minor"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => scheduleBlurCommit()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit(draft);
            }
          }}
        />
      </div>
      {open && (
        <ul id={`${id}-major-listbox`} className="profile-major-results" role="listbox" aria-label="Majors">
          {filtered.length === 0 ? (
            <li className="profile-major-hint">No matches. Press Enter to keep your text.</li>
          ) : (
            filtered.map((major) => (
              <li key={major} role="presentation">
                <button
                  type="button"
                  role="option"
                  className="profile-major-option"
                  aria-selected={value === major}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    clearBlurTimer();
                    commit(major);
                  }}
                >
                  {major}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};
